import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCookieBite, FaArrowLeft, FaTrash } from 'react-icons/fa';
import Header from './components/Header';
import { useLanguage } from './i18n';

const CookiePolicyPage = () => {
  const { t, tArray } = useLanguage();

  useEffect(() => {
    document.title = t('cookiePolicy.seo.title');
    window.scrollTo(0, 0);
  }, [t]);

  const cookieTypes = tArray('cookiePolicy.cookieTypes');

  const clearCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-6 shadow-lg shadow-orange-500/30">
              <FaCookieBite className="text-4xl text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('cookiePolicy.title')}
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              {t('cookiePolicy.subtitle')}
            </p>
            <p className="text-gray-500">
              {t('cookiePolicy.lastUpdated')}: {t('cookiePolicy.updateDate')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 mb-8 transition-colors"
            >
              <FaArrowLeft />
              {t('cookiePolicy.backHome')}
            </Link>

            {/* Policy Content */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 p-8 md:p-12">
              {/* What are cookies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.whatAreCookies.title')}
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  {t('cookiePolicy.whatAreCookies.content')}
                </p>
              </div>

              {/* How we use cookies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.howWeUse.title')}
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  {t('cookiePolicy.howWeUse.content')}
                </p>
              </div>

              {/* Types of Cookies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {t('cookiePolicy.typesTitle')}
                </h2>
                <div className="space-y-6">
                  {cookieTypes.map((cookie, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-900/50 rounded-xl p-6 border border-gray-700"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold text-white">
                          {cookie.name}
                        </h3>
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                          cookie.required
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {cookie.required ? t('cookiePolicy.required') : t('cookiePolicy.optional')}
                        </span>
                      </div>
                      <p className="text-gray-400 mb-3">
                        {cookie.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span><strong>{t('cookiePolicy.duration')}:</strong> {cookie.duration}</span>
                        <span><strong>{t('cookiePolicy.purpose')}:</strong> {cookie.purpose}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Managing Cookies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.managing.title')}
                </h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {t('cookiePolicy.managing.content')}
                </p>

                {/* Reset Cookie Preferences Button */}
                <button
                  onClick={clearCookieConsent}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all"
                >
                  <FaTrash />
                  {t('cookiePolicy.managing.resetButton')}
                </button>
              </div>

              {/* Third Party Cookies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.thirdParty.title')}
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  {t('cookiePolicy.thirdParty.content')}
                </p>
              </div>

              {/* Contact Section */}
              <div className="mt-12 pt-8 border-t border-gray-700">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.contact.title')}
                </h2>
                <p className="text-gray-300 mb-4">
                  {t('cookiePolicy.contact.description')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all"
                  >
                    {t('cookiePolicy.contact.button')}
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all"
                  >
                    {t('cookiePolicy.contact.privacyPolicy')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="container mx-auto px-4 text-center text-gray-500">
          <p>&copy; {new Date().getFullYear()} Tunnel Coaching 1-1. {t('footer.designedBy')} Glotone</p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicyPage;
